import { Component, h, Prop, Element, State, Event, EventEmitter } from '@stencil/core';
import { DynamicOverlay, presetAttributes } from '../../../../../interfaces';
import { timeToSeconds, parseColor } from '../../../../../util';

@Component({
  tag: 'presto-dynamic-overlays',
  styleUrl: 'presto-dynamic-overlays.scss',
  shadow: true,
})
export class PrestoDynamicOverlays {
  private topLeft: HTMLDivElement;
  private topRight: HTMLDivElement;
  private container: HTMLDivElement;
  private watermarkRef: {
    left: HTMLPrestoDynamicOverlayUiElement;
    right: HTMLPrestoDynamicOverlayUiElement;
  } = {
    left: null,
    right: null,
  };

  @Element() el: HTMLPrestoDynamicOverlaysElement;

  @Prop() overlays: Array<DynamicOverlay>;
  @Prop() player: any;
  @Prop() preset: presetAttributes;
  @Prop() enabled: boolean;

  @State() currentTime: number;
  @State() destroy: boolean = false;

  @Event() reloadComponent: EventEmitter<void>;

  private refs: any = {};

  componentDidLoad() {
    if (!this.player) {
      return;
    }
    this.player.on('timeupdate', e => {
      this.currentTime = e.detail.plyr.currentTime;
      this.checkValidity(); // check overlays validity.
    });
  }

  /**
   * Check validity of the overlays.
   * Blow up if any funny business.
   */
  checkValidity() {
    // only if we are playing
    if (!this.player.playing) {
      return;
    }

    if (!this?.overlays?.length && !this.preset?.watermark?.enabled) {
      return;
    }

    // make sure container and other divs are not removed.
    if (!this.container?.offsetParent || !this.topLeft?.offsetParent || !this.topRight?.offsetParent) {
      this.reloadComponent.emit();
    }

    // check for funny business on overlays.
    Object.keys(this.refs || {}).forEach(key => {
      const { overlay, component } = this.refs[key];
      this.checkComponent(component, overlay.text, () => this.reloadComponent.emit());
    });

    // check for any funny business on watermark
    if (this.shouldShowWatermark('top-left') && this.watermarkRef.left) {
      this.checkComponent(this.watermarkRef.left, this.preset?.watermark?.text, () => this.reloadComponent.emit());
    }
    if (this.shouldShowWatermark('top-right') && this.watermarkRef.right) {
      this.checkComponent(this.watermarkRef.right, this.preset?.watermark?.text, () => this.reloadComponent.emit());
    }
  }

  /**
   * Check if the component is valid.
   * If invalid, run a callback.
   *
   * @param component
   * @param text
   * @returns
   */
  checkComponent(component, text, callback) {
    // hidden, it's invalid.
    if (!component?.offsetParent) {
      return callback();
    }

    // slot was removed.
    if (!component.shadowRoot.querySelector('slot')) {
      return callback();
    }

    // only if we're playing
    if (!!this.player && this.player.playing) {
      const content = component.shadowRoot.querySelector('slot').assignedNodes()[0];

      if (content) {
        // Slot content changed. Watermark/overlay text is rendered via innerHTML
        // (see renderOverlay / render), so compare the live innerHTML against the
        // same text after a parse+serialize round-trip. The browser normalizes on
        // serialization (bare '&' -> '&amp;', entity/attribute rewrites), so a raw
        // comparison would always mismatch on '&', '<', '>' and tear the overlay
        // down every tick; the round-trip makes both sides canonical while still
        // catching real markup tampering.
        const expected = document.createElement('div');
        expected.innerHTML = String(text ?? '');
        if (content.parentElement.innerHTML !== expected.innerHTML) {
          return callback();
        }

        // font-size changes.
        const style = getComputedStyle(content.parentElement.shadowRoot.querySelector('.overlay-text'));
        if (parseInt(style.fontSize, 10) < 10) {
          return callback();
        }

        // opacity changes
        const color = parseColor(style.color);
        if (color?.[3] !== '1') {
          return callback();
        }
      }
    }
  }

  /**
   * Show the overlay
   * @param overlay
   * @returns
   */
  shouldShowOverlay(overlay) {
    // need a time.
    if (typeof this.currentTime === 'undefined') {
      return;
    }

    // bail if current time is less than start time
    if (this.currentTime < timeToSeconds(overlay?.startTime)) {
      return false;
    }

    // bail if current time is more than end time
    if (this.currentTime > timeToSeconds(overlay?.endTime)) {
      return false;
    }

    return true;
  }

  /**
   * Render the watermark
   */
  renderOverlay(overlay) {
    // innerHTML so stored formatting renders: overlay text is wp_kses_post-sanitized
    // on save (DynamicData::replaceItems) and the raw-tag XSS path is closed at the
    // kses layer in Compatibility::allowHtml(), matching CTA/email. Hidden overlays
    // pass '' so the host has no slotted node and checkComponent skips them.
    return (
      <presto-dynamic-overlay-ui
        class={{
          visible: this.shouldShowOverlay(overlay),
        }}
        ref={el =>
          (this.refs[overlay.id] = {
            overlay,
            component: el as HTMLPrestoDynamicOverlayUiElement,
          })
        }
        key={overlay.id}
        position={overlay.position}
        href={overlay?.link?.url}
        target={overlay?.link?.opensInNewTab ? '_blank' : '_self'}
        style={{
          '--presto-dynamic-overlay-color': overlay?.color || '#fff',
          '--presto-dynamic-overlay-background': overlay?.backgroundColor || '#333',
          '--presto-dynamic-overlay-opacity': overlay?.opacity ? (overlay.opacity / 100).toString() : '1',
        }}
        innerHTML={this.shouldShowOverlay(overlay) ? overlay.text : ''}
      ></presto-dynamic-overlay-ui>
    );
  }

  /**
   * Should we show the watermark?
   */
  shouldShowWatermark(position) {
    if (!this.preset?.watermark?.enabled) return false;

    const watermark = this.preset?.watermark;

    // randomize
    if (watermark.position === 'randomize') {
      const time = Math.floor((this.player.currentTime || 0) / 10);

      // even or odd
      if (time % 2 == 0) {
        return position === 'top-left';
      } else {
        return position === 'top-right';
      }
    }

    // position is set.
    if (watermark.position === position) {
      return true;
    }

    // fallback.
    if (!watermark.position && 'top-right' === position) {
      return true;
    }

    return false;
  }

  render() {
    // bail if disabled.
    if (!this.enabled) {
      return;
    }

    // bail if no overlays or watermark
    if (!this?.overlays?.length && !this.preset?.watermark?.enabled) {
      return;
    }

    return (
      <div class="overlays" ref={el => (this.container = el as HTMLDivElement)}>
        <div class="top-left" ref={el => (this.topLeft = el as HTMLDivElement)}>
          {!!this.shouldShowWatermark('top-left') && (
            <presto-dynamic-overlay-ui
              ref={el => (this.watermarkRef.left = el as HTMLPrestoDynamicOverlayUiElement)}
              style={{
                '--presto-dynamic-overlay-color': this.preset.watermark?.color || '#fff',
                '--presto-dynamic-overlay-background': this.preset.watermark?.backgroundColor || '#333',
                '--presto-dynamic-overlay-opacity': this.preset.watermark?.opacity ? (this.preset.watermark.opacity / 100).toString() : '1',
              }}
              class="visible"
              position={'top-left'}
              innerHTML={this.preset.watermark.text}
            ></presto-dynamic-overlay-ui>
          )}

          {!!this?.overlays?.length &&
            this.overlays.map(overlay => {
              if (overlay.position !== 'top-left') {
                return '';
              }
              return this.renderOverlay(overlay);
            })}
        </div>

        <div class="top-right" ref={el => (this.topRight = el as HTMLDivElement)}>
          {!!this.shouldShowWatermark('top-right') && (
            <presto-dynamic-overlay-ui
              ref={el => (this.watermarkRef.right = el as HTMLPrestoDynamicOverlayUiElement)}
              style={{
                '--presto-dynamic-overlay-color': this.preset.watermark?.color || '#fff',
                '--presto-dynamic-overlay-background': this.preset.watermark?.backgroundColor || '#333',
                '--presto-dynamic-overlay-opacity': this.preset.watermark?.opacity ? (this.preset.watermark.opacity / 100).toString() : '1',
              }}
              class="visible"
              position={'top-right'}
              innerHTML={this.preset.watermark.text}
            ></presto-dynamic-overlay-ui>
          )}

          {!!this?.overlays?.length &&
            this.overlays.map(overlay => {
              if (overlay.position !== 'top-right') {
                return '';
              }
              return this.renderOverlay(overlay);
            })}
        </div>
      </div>
    );
  }
}
